/**
 * Shared SVG path building utilities for play rendering
 * Consolidates logic from AnimatedRoute, PathRenderer, and PlayThumbnailSVG
 */

import type { ControlPoint, Coordinate, Drawing, PathSegment } from '@/types'
import { getSegmentPoints, getSmoothedPoints } from './Drawing.utils'
import { ARROW_ANGLE_DEGREES, ARROW_LENGTH_MULTIPLIER, TSHAPE_LENGTH_MULTIPLIER } from '@/constants'
import { angleBetweenPoints, distance } from './Geometry.utils'
import { cubicLength, quadraticLength } from './Bezier.utils'

export type PathBuildResult = {
	d: string
	pathLength?: number
	startDirection?: { angle: number; point: Coordinate }
	endDirection?: { angle: number; point: Coordinate }
	endPoints: Coordinate[]
}

/**
 * Build SVG path string from drawing with optional length calculation
 */
export function buildSVGPath(
	drawing: Drawing,
	transformPoint: (point: Coordinate) => Coordinate,
	options?: { calculateLength?: boolean },
): PathBuildResult {
	if (!drawing.segments || drawing.segments.length === 0) {
		return { d: '', pathLength: 0, endPoints: [] }
	}

	const calculateLength = options?.calculateLength ?? false

	// Try smooth path first (curve mode)
	const smoothResult = buildSmoothPath(drawing, transformPoint, calculateLength)
	if (smoothResult) {
		return smoothResult
	}

	// Fall back to segmented path (sharp mode, cubic, quadratic)
	return buildSegmentedPath(drawing, transformPoint, calculateLength)
}

/**
 * Build smooth path using Chaikin algorithm (curve mode)
 */
function buildSmoothPath(
	drawing: Drawing,
	transformPoint: (point: Coordinate) => Coordinate,
	calculateLength: boolean,
): PathBuildResult | null {
	const smoothed = getSmoothedPoints(drawing, transformPoint)
	if (!smoothed || smoothed.length < 2) {
		return null
	}

	const commands: string[] = [`M ${smoothed[0]!.x} ${smoothed[0]!.y}`]
	let pathLength = 0

	for (let i = 1; i < smoothed.length; i++) {
		const curr = smoothed[i]!
		const prev = smoothed[i - 1]!
		commands.push(`L ${curr.x} ${curr.y}`)

		if (calculateLength) {
			pathLength += distance(prev, curr)
		}
	}

	// Calculate start direction (pointing outward from start)
	const firstSmoothed = smoothed[0]!
	const secondSmoothed = smoothed[1]!
	const startAngle = angleBetweenPoints(secondSmoothed, firstSmoothed)

	// Calculate end direction from smoothed path
	const lastSmoothed = smoothed[smoothed.length - 1]!
	const prevSmoothed = smoothed[smoothed.length - 2]!
	const endAngle = angleBetweenPoints(prevSmoothed, lastSmoothed)

	return {
		d: commands.join(' '),
		pathLength: calculateLength ? pathLength : undefined,
		endPoints: [firstSmoothed, lastSmoothed],
		startDirection: { angle: startAngle, point: firstSmoothed },
		endDirection: { angle: endAngle, point: lastSmoothed },
	}
}

/**
 * Build segmented path (sharp mode, cubic, quadratic)
 */
function buildSegmentedPath(
	drawing: Drawing,
	transformPoint: (point: Coordinate) => Coordinate,
	calculateLength: boolean,
): PathBuildResult {
	const commands: string[] = []
	const endPoints: Coordinate[] = []
	let pathLength = 0
	let prevPoint: Coordinate | null = null
	let startDirection: { angle: number; point: Coordinate } | undefined
	let endDirection: { angle: number; point: Coordinate } | undefined

	for (let i = 0; i < drawing.segments.length; i++) {
		const segment = drawing.segments[i]
		if (!segment) continue

		const points = getSegmentPoints(drawing, segment)
		if (points.length === 0) continue

		if (i === 0) {
			const first = transformPoint(points[0]!)
			commands.push(`M ${first.x} ${first.y}`)
			prevPoint = first
		}

		if (segment.type === 'line') {
			const result = buildLineSegment(points, transformPoint, calculateLength, prevPoint)
			commands.push(result.command)
			endPoints.push(result.endPoint)
			pathLength += result.length
			prevPoint = result.endPoint
		} else if (segment.type === 'quadratic') {
			const result = buildQuadraticSegment(points, transformPoint, calculateLength, prevPoint)
			if (result) {
				commands.push(result.command)
				endPoints.push(result.endPoint)
				pathLength += result.length
				prevPoint = result.endPoint
			}
		} else if (segment.type === 'cubic') {
			const result = buildCubicSegment(segment, drawing.points, transformPoint, calculateLength, prevPoint)
			if (result) {
				commands.push(result.command)
				endPoints.push(result.endPoint)
				pathLength += result.length
				prevPoint = result.endPoint
			}
		}

		// Calculate start direction (pointing outward from start)
		if (i === 0 && points.length >= 2) {
			const pixelPoints = points.map(transformPoint)
			const firstPt = pixelPoints[0]!
			const secondPt = pixelPoints[1]!
			const angle = angleBetweenPoints(secondPt, firstPt)
			startDirection = { angle, point: firstPt }
		}

		// Calculate end direction for arrow rendering
		if (i === drawing.segments.length - 1 && points.length >= 2) {
			const pixelPoints = points.map(transformPoint)
			const lastPt = pixelPoints[pixelPoints.length - 1]!
			const prevPt = pixelPoints[pixelPoints.length - 2]!
			const angle = angleBetweenPoints(prevPt, lastPt)
			endDirection = { angle, point: lastPt }
		}
	}

	return {
		d: commands.join(' '),
		pathLength: calculateLength ? pathLength : undefined,
		endPoints,
		startDirection,
		endDirection,
	}
}

/**
 * Build line segment
 */
function buildLineSegment(
	points: ControlPoint[],
	transformPoint: (point: Coordinate) => Coordinate,
	calculateLength: boolean,
	prevPoint: Coordinate | null,
): { command: string; endPoint: Coordinate; length: number } {
	const last = points[points.length - 1]!
	const target = transformPoint(last)
	const length = calculateLength && prevPoint ? distance(prevPoint, target) : 0

	return {
		command: `L ${target.x} ${target.y}`,
		endPoint: target,
		length,
	}
}

/**
 * Build quadratic Bezier segment
 */
function buildQuadraticSegment(
	points: ControlPoint[],
	transformPoint: (point: Coordinate) => Coordinate,
	calculateLength: boolean,
	prevPoint: Coordinate | null,
): { command: string; endPoint: Coordinate; length: number } | null {
	if (points.length < 2) return null

	const control = transformPoint(points[0]!)
	const end = transformPoint(points[1]!)
	const length = calculateLength && prevPoint
		? quadraticLength(prevPoint, control, end)
		: 0

	return {
		command: `Q ${control.x} ${control.y} ${end.x} ${end.y}`,
		endPoint: end,
		length,
	}
}

/**
 * Build cubic Bezier segment - handles BOTH new and old formats
 * NEW: pointIds = [fromId, toId] with handleIn/handleOut in nodes
 * OLD: pointIds = [cp1Id, cp2Id, endId] (backward compatibility)
 */
function buildCubicSegment(
	segment: PathSegment,
	points: Record<string, ControlPoint>,
	transformPoint: (point: Coordinate) => Coordinate,
	calculateLength: boolean,
	prevPoint: Coordinate | null,
): { command: string; endPoint: Coordinate; length: number } | null {
	if (segment.pointIds.length === 2) {
		// NEW FORMAT: handles stored in nodes
		const fromPoint = points[segment.pointIds[0]!]
		const toPoint = points[segment.pointIds[1]!]
		if (!fromPoint || !toPoint) return null

		const cp1: Coordinate = {
			x: fromPoint.x + (fromPoint.handleOut?.x ?? 0),
			y: fromPoint.y + (fromPoint.handleOut?.y ?? 0),
		}
		const cp2: Coordinate = {
			x: toPoint.x + (toPoint.handleIn?.x ?? 0),
			y: toPoint.y + (toPoint.handleIn?.y ?? 0),
		}

		const c1 = transformPoint(cp1)
		const c2 = transformPoint(cp2)
		const end = transformPoint(toPoint)
		const length = calculateLength && prevPoint
			? cubicLength(prevPoint, c1, c2, end)
			: 0

		return {
			command: `C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${end.x} ${end.y}`,
			endPoint: end,
			length,
		}
	} else if (segment.pointIds.length >= 3) {
		// OLD FORMAT: control points as separate nodes
		const cp1Point = points[segment.pointIds[0]!]
		const cp2Point = points[segment.pointIds[1]!]
		const endPoint = points[segment.pointIds[2]!]
		if (!cp1Point || !cp2Point || !endPoint) return null

		const c1 = transformPoint(cp1Point)
		const c2 = transformPoint(cp2Point)
		const end = transformPoint(endPoint)
		const length = calculateLength && prevPoint
			? cubicLength(prevPoint, c1, c2, end)
			: 0

		return {
			command: `C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${end.x} ${end.y}`,
			endPoint: end,
			length,
		}
	}

	return null
}

/**
 * Build filled arrow — two paddle/teardrop shapes that meet at the tip.
 * Each arm is a filled rectangle with a semicircular cap at the far end.
 * Returns SVG path d-strings for each filled shape.
 */
export function buildFilledArrow(
	endPoint: Coordinate,
	angle: number,
	strokeWidth: number,
): string[] {
	const length = strokeWidth * ARROW_LENGTH_MULTIPLIER
	const halfW = strokeWidth / 2
	const armAngle1 = angle - ARROW_ANGLE_DEGREES
	const armAngle2 = angle + ARROW_ANGLE_DEGREES

	return [
		buildFilledArm(endPoint, armAngle1, length, halfW),
		buildFilledArm(endPoint, armAngle2, length, halfW),
	]
}

/**
 * Build a single filled arrow arm (rectangle + semicircular cap)
 */
function buildFilledArm(
	endPoint: Coordinate,
	armAngle: number,
	length: number,
	halfW: number,
): string {
	// Tip center is at the far end of the arm
	const tipCenter = {
		x: endPoint.x - length * Math.cos(armAngle),
		y: endPoint.y - length * Math.sin(armAngle),
	}

	// Perpendicular direction for the arm width
	const perpX = Math.cos(armAngle + Math.PI / 2)
	const perpY = Math.sin(armAngle + Math.PI / 2)

	// Four corners of the arm rectangle
	const innerA = { x: endPoint.x + halfW * perpX, y: endPoint.y + halfW * perpY }
	const innerB = { x: endPoint.x - halfW * perpX, y: endPoint.y - halfW * perpY }
	const outerB = { x: tipCenter.x - halfW * perpX, y: tipCenter.y - halfW * perpY }
	const outerA = { x: tipCenter.x + halfW * perpX, y: tipCenter.y + halfW * perpY }

	// Rectangle + semicircular arc at the tip
	return [
		`M ${innerB.x} ${innerB.y}`,
		`L ${outerB.x} ${outerB.y}`,
		`A ${halfW} ${halfW} 0 0 0 ${outerA.x} ${outerA.y}`,
		`L ${innerA.x} ${innerA.y}`,
		'Z',
	].join(' ')
}

/**
 * Build filled T-shape — a capsule (stadium shape) perpendicular to the route.
 * Returns SVG path d-strings for each filled shape.
 */
export function buildFilledTShape(
	endPoint: Coordinate,
	angle: number,
	strokeWidth: number,
): string[] {
	const barHalfLength = strokeWidth * TSHAPE_LENGTH_MULTIPLIER
	const halfW = strokeWidth / 2

	// Perpendicular and route directions
	const perpX = Math.cos(angle + Math.PI / 2)
	const perpY = Math.sin(angle + Math.PI / 2)
	const routeX = Math.cos(angle)
	const routeY = Math.sin(angle)

	// Centers of the bar ends
	const leftCenter = {
		x: endPoint.x - barHalfLength * perpX,
		y: endPoint.y - barHalfLength * perpY,
	}
	const rightCenter = {
		x: endPoint.x + barHalfLength * perpX,
		y: endPoint.y + barHalfLength * perpY,
	}

	// Four corners offset by halfW along the route direction
	const leftBack = { x: leftCenter.x - halfW * routeX, y: leftCenter.y - halfW * routeY }
	const leftFront = { x: leftCenter.x + halfW * routeX, y: leftCenter.y + halfW * routeY }
	const rightFront = { x: rightCenter.x + halfW * routeX, y: rightCenter.y + halfW * routeY }
	const rightBack = { x: rightCenter.x - halfW * routeX, y: rightCenter.y - halfW * routeY }

	// Capsule: rectangle with semicircular arcs at each end
	const capsule = [
		`M ${leftBack.x} ${leftBack.y}`,
		`A ${halfW} ${halfW} 0 0 0 ${leftFront.x} ${leftFront.y}`,
		`L ${rightFront.x} ${rightFront.y}`,
		`A ${halfW} ${halfW} 0 0 0 ${rightBack.x} ${rightBack.y}`,
		'Z',
	].join(' ')

	return [capsule]
}
